import { useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { CasePropertiesValue } from "src/clients/case-management";
import { logger } from "src/utils/logger";

export type CreateCaseFormValues = {
  source?: string;
  comment?: string;
  file_category_files: Record<string, string[]>;
  case_properties: Record<string, unknown>;
  status?: string;
  sub_status?: string;
  assignee_id?: string;
};

export type CreateCaseStepId = "context" | "properties" | "case-routing";

export type CreateCaseStep = {
  id: CreateCaseStepId;
  title: string;
  formTitle: string;
  description: string;
};

export const CREATE_CASE_STEPS: CreateCaseStep[] = [
  {
    id: "context",
    title: "Context",
    formTitle: "Define the Case context",
    description:
      "Select where this Case came from, note why you're opening it, and attach any supporting files",
  },
  {
    id: "properties",
    title: "Properties",
    formTitle: "Map Case properties",
    description:
      "Provide value for properties to capture the details of this Case",
  },
  {
    id: "case-routing",
    title: "Case routing",
    formTitle: "Set status and assignee",
    description:
      "Choose the initial status and optionally assign a reviewer to the Case",
  },
];

export const getCreateCaseStepById = (
  stepId: CreateCaseStepId,
): CreateCaseStep => {
  const step = CREATE_CASE_STEPS.find((step) => step.id === stepId);

  if (!step) {
    logger.error(`Create case step with id "${stepId}" not found`);
  }

  return step ?? CREATE_CASE_STEPS[0];
};

export const useCurrentCreateCaseStep = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStepId = searchParams.get("step");
  const isValidStep =
    rawStepId && CREATE_CASE_STEPS.some((step) => step.id === rawStepId);

  if (rawStepId && !isValidStep) {
    logger.warn(
      `Invalid create case step ID "${rawStepId}" in URL. Falling back to default step.`,
    );
  }

  const currentStepId = isValidStep
    ? (rawStepId as CreateCaseStepId)
    : CREATE_CASE_STEPS[0].id;

  const setCurrentStep = (stepId: CreateCaseStepId) => {
    const stepExists = CREATE_CASE_STEPS.some((step) => step.id === stepId);
    if (!stepExists) {
      logger.error(
        `Attempted to navigate to create case step "${stepId}" which does not exist.`,
      );
      return;
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("step", stepId);
      return newParams;
    });
  };

  return [currentStepId, setCurrentStep] as const;
};

const getPropertySchema = (property: CasePropertiesValue): yup.AnySchema => {
  switch (property.type) {
    case "number":
      return yup
        .number()
        .nullable()
        .transform((_, originalValue) =>
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
            ? null
            : Number(originalValue),
        )
        .typeError(`${property.display_name} must be a number`);

    case "integer":
      return yup
        .number()
        .nullable()
        .transform((_, originalValue) =>
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
            ? null
            : Number(originalValue),
        )
        .typeError(`${property.display_name} must be a number`)
        .integer(`${property.display_name} must be a non-decimal number`);

    case "string":
    case "entity_ref":
    case "event_ref":
    case "enum":
      return yup.string().nullable();

    case "tags":
      return yup.array().of(yup.string()).nullable();

    case "datetime":
      return yup.mixed().nullable();
  }
};

export const buildCreateCaseSchema = (
  caseProperties: Record<string, CasePropertiesValue> | undefined,
  sourceRequired: boolean,
  statusRequired: boolean,
) => {
  const propertiesShape: Record<string, yup.AnySchema> = {};

  if (caseProperties) {
    for (const [key, property] of Object.entries(caseProperties)) {
      propertiesShape[key] = getPropertySchema(property);
    }
  }

  return yup.object({
    source: sourceRequired
      ? yup.string().required("Source is required")
      : yup.string().nullable(),
    comment: yup.string().nullable(),
    file_category_files: yup.object(),
    case_properties: yup.object(propertiesShape),
    status: statusRequired
      ? yup.string().required("Status is required")
      : yup.string().nullable(),
    sub_status: yup.string().nullable(),
    assignee_id: yup.string().nullable(),
  });
};
